/**
 * 「花费总览」页（`/spend-overview`）的 URL 状态（2026-10-05 第十九批 · 用户第 2 条）。
 *
 * 沿用 `urlState.ts` 的三条原则：
 *   1) URL 是页面筛选状态的**唯一真相**（可分享、刷新还原、前进后退一致）；
 *   2) 默认值不写入 URL（`/spend-overview` 与 `?granularity=day` 是两种不同的可分享状态）；
 *   3) 解析对垃圾输入免疫（用户会手改 URL），**绝不抛错**。
 *
 * ## 字段
 *
 *  · `granularity`：`month`（默认）/ `day` —— 两个标签页；
 *  · `months`：月视图行数（默认 **6** = 最近 6 个自然月，1..24）；
 *  · `days`：日视图行数（默认 **30** = 最近 30 天，1..180）；
 *  · `scope`：`brand`（默认，**品牌合计**）/ `shop`（仅该店铺）；
 *  · `shop`：页面上选中的店铺（空 = 跟随右上角全局选择；写进 URL 让链接可分享）；
 *  · `to`：窗口右端业务日（空 = **跟随数据就绪右端**；显式给值用于复现某个已结算窗口）。
 *
 * ⚠️ 用户口径：**选店铺不改变数据范围**（`scope` 仍是 `brand`，数据是品牌合计），
 * 店铺选择器起「筛选」作用；只有 `scope=shop` 才真的只看该店。
 */

import { isValidIsoDate } from "./dateRange.js";

export type SpendOverviewGranularity = "month" | "day";
export type SpendOverviewScope = "brand" | "shop";

export interface SpendOverviewSearch {
  readonly granularity: SpendOverviewGranularity;
  readonly months: number;
  readonly days: number;
  readonly scope: SpendOverviewScope;
  /** 选中的店铺；`""` = 跟随右上角全局选择。 */
  readonly shop: string;
  /** 窗口右端业务日；`""` = 跟随数据就绪右端。 */
  readonly to: string;
}

/** 月视图默认行数（与契约 `SPEND_OVERVIEW_MONTH_COUNT` 同值）。 */
export const SPEND_OVERVIEW_DEFAULT_MONTHS = 6;
/** 日视图默认行数（与契约 `SPEND_OVERVIEW_DAY_COUNT` 同值）。 */
export const SPEND_OVERVIEW_DEFAULT_DAYS = 30;
/** 行数上限（与服务端钳制上限同值；URL 里超限的值在解析时钳回）。 */
export const SPEND_OVERVIEW_MAX_MONTHS = 24;
export const SPEND_OVERVIEW_MAX_DAYS = 180;

export const DEFAULT_SPEND_OVERVIEW_SEARCH: SpendOverviewSearch = {
  granularity: "month",
  months: SPEND_OVERVIEW_DEFAULT_MONTHS,
  days: SPEND_OVERVIEW_DEFAULT_DAYS,
  scope: "brand",
  shop: "",
  to: "",
};

/** 共享的 URL key（`router.tsx` 的 `stringifySearch` 用它识别本页 schema）。 */
export const SPEND_OVERVIEW_SEARCH_KEYS: readonly string[] = [
  "granularity",
  "months",
  "days",
  "scope",
  "shop",
  "to",
];

function readString(raw: Record<string, unknown>, key: string): string | null {
  const value = raw[key];
  return typeof value === "string" ? value : null;
}

function readInt(raw: Record<string, unknown>, key: string, fallback: number, max: number): number {
  const value = raw[key];
  const parsed =
    typeof value === "number" ? value : typeof value === "string" ? Number(value) : Number.NaN;
  if (!Number.isFinite(parsed)) return fallback;
  const truncated = Math.trunc(parsed);
  if (truncated < 1) return fallback;
  return Math.min(max, truncated);
}

/** URL → 状态（垃圾输入一律回落默认值，绝不抛错）。 */
export function parseSpendOverviewSearch(raw: Record<string, unknown>): SpendOverviewSearch {
  const granularity = readString(raw, "granularity");
  const scope = readString(raw, "scope");
  const shop = (readString(raw, "shop") ?? "").trim();
  const to = (readString(raw, "to") ?? "").trim();
  return {
    granularity: granularity === "day" ? "day" : "month",
    months: readInt(raw, "months", SPEND_OVERVIEW_DEFAULT_MONTHS, SPEND_OVERVIEW_MAX_MONTHS),
    days: readInt(raw, "days", SPEND_OVERVIEW_DEFAULT_DAYS, SPEND_OVERVIEW_MAX_DAYS),
    scope: scope === "shop" ? "shop" : "brand",
    shop: shop.slice(0, 120),
    // 右端必须是**真实存在**的业务日；垃圾值 → 跟随就绪右端（不是回落成今天）。
    to: isValidIsoDate(to) ? to : "",
  };
}

/** 不可变局部更新（按同一套规则裁剪）。 */
export function patchSpendOverviewSearch(
  prev: SpendOverviewSearch,
  patch: Partial<SpendOverviewSearch>,
): SpendOverviewSearch {
  return parseSpendOverviewSearch({ ...prev, ...patch });
}

/** 序列化为 query 参数；等于默认值的字段一律省略（URL 短且可手写）。 */
export function spendOverviewSearchToParams(search: SpendOverviewSearch): Record<string, string> {
  const params: Record<string, string> = {};
  if (search.granularity !== DEFAULT_SPEND_OVERVIEW_SEARCH.granularity) {
    params["granularity"] = search.granularity;
  }
  if (search.months !== DEFAULT_SPEND_OVERVIEW_SEARCH.months) params["months"] = String(search.months);
  if (search.days !== DEFAULT_SPEND_OVERVIEW_SEARCH.days) params["days"] = String(search.days);
  if (search.scope !== DEFAULT_SPEND_OVERVIEW_SEARCH.scope) params["scope"] = search.scope;
  if (search.shop !== DEFAULT_SPEND_OVERVIEW_SEARCH.shop) params["shop"] = search.shop;
  if (search.to !== DEFAULT_SPEND_OVERVIEW_SEARCH.to) params["to"] = search.to;
  return params;
}

/** 供 TanStack Router 的 `stringifySearch` 使用。 */
export function spendOverviewSearchToQueryString(search: object): string {
  const params = spendOverviewSearchToParams(parseSpendOverviewSearch(search as Record<string, unknown>));
  const queryString = new URLSearchParams(params).toString();
  return queryString.length > 0 ? `?${queryString}` : "";
}

/** 当前状态与默认值的差异摘要（页面顶部「当前口径」用）。 */
export function describeSpendOverviewScope(search: SpendOverviewSearch, shopLabelText: string): string {
  if (search.scope === "shop") return `范围：仅 ${shopLabelText}`;
  return "范围：品牌合计";
}
